import React, { useState } from "react";
import { MainPolicySettingBlock, PolicyCustomizeSection } from "uis/Section";
import UI from "@mile-meeting/mile-ui";
import { useToast } from "hook/useToast";
import AlertModal from "uis/modal/alert-modal";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { getToggleState } from "utils/meetingSettingUtils.service";
import MeetingRoomPolicyModal from "./MeetingRoomPolicyModal/MeetingRoomPolicyModal";
import { MeetingRoomSetting } from "./MeetingRoomPolicyModal/types";
import {
  getTabletAutoCheckInDetail,
  getTabletAutoCheckInSoundAlertDetail,
  updateTabletAutoCheckIn,
  updateTabletAutoCheckInDetail,
  updateTabletAutoCheckInSoundAlertDetail,
} from "apis/meeting-settings/tablet";
import ToolTip from "uis/new-tootip";

type CheckInPolicySettingProps = {
  hasMeetingRoomUsedTablet: boolean;
  noShowEnabled: GLOBAL_MEETING_SETTING_VALUE;
  autoCheckInActive: GLOBAL_MEETING_SETTING_VALUE;
  noShowWarningSoundAlertActive: GLOBAL_MEETING_SETTING_VALUE;
  onChangeNoShowWarningSoundAlert: (active: GLOBAL_MEETING_SETTING_VALUE) => void;
  onChangeAutoCheckInActive: (active: GLOBAL_MEETING_SETTING_VALUE) => void;
  onUpdate: () => void;
};

const CheckInPolicySetting: React.FC<CheckInPolicySettingProps> = ({
  hasMeetingRoomUsedTablet,
  noShowEnabled,
  autoCheckInActive,
  noShowWarningSoundAlertActive,
  onChangeAutoCheckInActive,
  onChangeNoShowWarningSoundAlert,
  onUpdate,
}) => {
  const [autoCheckInActiveModal, setAutoCheckInActiveModal] = useState<boolean>(false);
  const [autoCheckInUnActiveModal, setAutoCheckInUnActiveModal] = useState<boolean>(false);
  const [openAutoCheckInDetailSettingModal, setOpenAutoCheckInDetailSettingModal] = useState<boolean>(false);
  const [openAutoCheckInSoundAlertDetailSettingModal, setOpenAutoCheckInSoundAlertDetailSettingModal] =
    useState<boolean>(false);
  const [autoCheckInSettingDetail, setAutoCheckInSettingDetail] = useState<MeetingRoomSetting[]>([]);
  const [autoCheckInSoundAlertSettingDetail, setAutoCheckInSoundAlertSettingDetail] = useState<MeetingRoomSetting[]>(
    []
  );
  const [openCheckInSoundAlertBulkConfirmModal, setOpenCheckInSoundAlertBulkConfirmModal] = useState<boolean>(false);

  const { showToast } = useToast();

  //* 자동 체크인 기능 상세 설정 조회
  const setTabletAutoCheckInDetailSetting = async () => {
    const res = await getTabletAutoCheckInDetail();
    if (res.success && res.data) {
      setAutoCheckInSettingDetail(res.data);
    }
  };

  //* 자동 체크인 기능 상세 설정 수정
  const onChangeTabletAutoCheckInDetailSetting = async (meetingSettings: MeetingRoomSetting[]) => {
    const res = await updateTabletAutoCheckInDetail(meetingSettings);
    if (res.success) {
      showToast("변경사항이 성공적으로 저장되었습니다.", "success");
      onUpdate();
      setOpenAutoCheckInDetailSettingModal(false);
    } else {
      showToast("변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  ///* 자동 체크인 소리 알림 상세 설정 조회
  const setTabletAutoCheckInSoundAlertDetailSetting = async () => {
    const res = await getTabletAutoCheckInSoundAlertDetail();
    if (res.success && res.data) {
      setAutoCheckInSoundAlertSettingDetail(res.data);
    }
  };

  //* 자동 체크인 소리 알림 상세 설정 수정
  const onChangeTabletAutoCheckInSoundAlertDetailSetting = async (meetingSettings: MeetingRoomSetting[]) => {
    const res = await updateTabletAutoCheckInSoundAlertDetail(meetingSettings);
    if (res.success) {
      showToast("변경사항이 성공적으로 저장되었습니다.", "success");
      onUpdate();
      setOpenAutoCheckInSoundAlertDetailSettingModal(false);
    } else {
      showToast("변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  return (
    <PolicyCustomizeSection
      title="체크인 기능 설정"
      rightChildren={
        <UI.ButtonGroups.Text
          title="체크인 기능 더 알아보기"
          size="small"
          onClick={() =>
            window.open(
              "https://mile-corporation.notion.site/12ea222a9c1b819b9c04cac8e4f1d746?pvs=25#232a222a9c1b8062b4a7f7718c6669e1"
            )
          }
        />
      }
    >
      <MainPolicySettingBlock
        title="자동 체크인 기능 활성화"
        tagLabel="BETA"
        tagType="secondary"
        showTag
        description="활성화 시 회의실 내부의 음성으로 회의 시작을 판단합니다."
        showAdvancedSettings
        onClickAdvancedSettings={() => {
          if (autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
            showToast("자동 체크인 기능을 활성화해야 상세 설정이 가능합니다.", "warning");
            return;
          }
          setTabletAutoCheckInDetailSetting();
          setOpenAutoCheckInDetailSettingModal(true);
        }}
        disabledAdvancedSettings={autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.OFF}
        rightChildren={
          <ToolTip
            text={
              autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.ON
                ? "전체 회의실 일괄 설정된 상태입니다."
                : autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                  ? "회의실별 상세 설정된 상태입니다."
                  : undefined
            }
            position={{
              top: -30,
              left: autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
            }}
            icon={
              <div style={{ position: "relative" }}>
                <UI.Selection.Toggle.TriState
                  size="large"
                  state={getToggleState(autoCheckInActive)}
                  onChange={() =>
                    autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                      ? setAutoCheckInActiveModal(true)
                      : setAutoCheckInUnActiveModal(true)
                  }
                  disabled={!hasMeetingRoomUsedTablet || noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF}
                />
                {(!hasMeetingRoomUsedTablet || noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF) && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={() => {
                      if (!hasMeetingRoomUsedTablet) {
                        showToast(
                          "자동 체크인 기능은 최소 1개 이상의 회의실이 내부 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                          "warning"
                        );
                      } else if (noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF) {
                        showToast(
                          "자동 체크인 기능은 '노쇼 정책 관리 > 노쇼 취소 기능' 활성화 시에만 사용 가능합니다.",
                          "warning"
                        );
                      }
                    }}
                  />
                )}
              </div>
            }
          />
        }
      />

      <MainPolicySettingBlock
        title="체크인 요청 소리 알림 활성화"
        description="체크인이 되지 않았을 경우, 노쇼 취소 1분 전에 태블릿에서 알림 소리가 울립니다."
        showBorderTop
        showAdvancedSettings
        onClickAdvancedSettings={() => {
          if (noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
            showToast("체크인 요청 소리 알림을 활성화해야 상세 설정이 가능합니다.", "warning");
            return;
          }
          setTabletAutoCheckInSoundAlertDetailSetting();
          setOpenAutoCheckInSoundAlertDetailSettingModal(true);
        }}
        disabledAdvancedSettings={noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF}
        rightChildren={
          <ToolTip
            text={
              noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON
                ? "전체 회의실 일괄 설정된 상태입니다."
                : noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                  ? "회의실별 상세 설정된 상태입니다."
                  : undefined
            }
            position={{
              top: -30,
              left: noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
            }}
            icon={
              <div style={{ position: "relative" }}>
                <UI.Selection.Toggle.TriState
                  size="large"
                  state={getToggleState(noShowWarningSoundAlertActive)}
                  onChange={() => {
                    if (noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL) {
                      setOpenCheckInSoundAlertBulkConfirmModal(true);
                      return;
                    }
                    onChangeNoShowWarningSoundAlert(
                      noShowWarningSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                        ? GLOBAL_MEETING_SETTING_VALUE.ON
                        : GLOBAL_MEETING_SETTING_VALUE.OFF
                    );
                  }}
                  disabled={!hasMeetingRoomUsedTablet || noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF}
                />
                {(!hasMeetingRoomUsedTablet || noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF) && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={() => {
                      if (!hasMeetingRoomUsedTablet) {
                        showToast(
                          "체크인 요청 소리 알림 기능은 최소 1개 이상의 회의실이 내부 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                          "warning"
                        );
                      } else if (noShowEnabled === GLOBAL_MEETING_SETTING_VALUE.OFF) {
                        showToast(
                          "노쇼 취소 전 소리 알림은 '노쇼 정책 관리 > 노쇼 취소 기능' 활성화 시에만 사용 가능합니다.",
                          "warning"
                        );
                      }
                    }}
                  />
                )}
              </div>
            }
          />
        }
      />
      <MeetingRoomPolicyModal
        modalIsOpen={openAutoCheckInDetailSettingModal}
        closeModal={() => setOpenAutoCheckInDetailSettingModal(false)}
        title="자동 체크인 기능 회의실별 상세 설정"
        settingData={autoCheckInSettingDetail}
        onUpdate={onChangeTabletAutoCheckInDetailSetting}
        metaData={[
          {
            key: "isAutoCheckInEnabled",
            label: `자동 체크인\n기능 활성화`,
            isMainPolicy: true,
            type: "toggle",
          },
        ]}
      />
      <MeetingRoomPolicyModal
        modalIsOpen={openAutoCheckInSoundAlertDetailSettingModal}
        closeModal={() => setOpenAutoCheckInSoundAlertDetailSettingModal(false)}
        title="체크인 요청 소리 알림 회의실별 상세 설정"
        settingData={autoCheckInSoundAlertSettingDetail}
        onUpdate={onChangeTabletAutoCheckInSoundAlertDetailSetting}
        metaData={[
          {
            key: "isNoShowSoundAlertEnabled",
            label: `체크인 요청 소리\n알림 활성화`,
            isMainPolicy: true,
            type: "toggle",
          },
        ]}
      />
      <AlertModal
        title={
          <p>
            자동 체크인 기능을
            <br />
            활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            음성 감지를 통해 체크인을 자동으로
            <br />
            처리할 수 있도록 보조합니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeAutoCheckInActive(GLOBAL_MEETING_SETTING_VALUE.ON);
          setAutoCheckInActiveModal(false);
        }}
        modalIsOpen={autoCheckInActiveModal}
        closeModal={() => setAutoCheckInActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            자동 체크인 기능을
            <br />
            {autoCheckInActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL ? "일괄" : ""} 비활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            '회의 시작하기' 버튼을 터치해야
            <br />
            회의가 시작되도록 설정합니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeAutoCheckInActive(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setAutoCheckInUnActiveModal(false);
        }}
        modalIsOpen={autoCheckInUnActiveModal}
        closeModal={() => setAutoCheckInUnActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            체크인 요청 소리 알림을
            <br />
            일괄 비활성화하시겠습니까?
          </p>
        }
        content="회의실별 상세 설정된 값이 일괄 변경됩니다."
        btnTitle="변경하기"
        onChange={() => {
          onChangeNoShowWarningSoundAlert(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setOpenCheckInSoundAlertBulkConfirmModal(false);
        }}
        modalIsOpen={openCheckInSoundAlertBulkConfirmModal}
        closeModal={() => setOpenCheckInSoundAlertBulkConfirmModal(false)}
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(CheckInPolicySetting);
